import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import BASE_URL from "../../Utilities/BASE_URL";

const STATUS_OPTIONS = [
  "All",
  "Warehouse",
  "Loaded",
  "Active",
  "In transit",
  "Invoice Submitted",
  "Completed",
];

const TrackingList = () => {
  const navigate = useNavigate();
  const { isLoggedIn, token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const fetchJobs = async () => {
      if (!token) return;
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${BASE_URL}/auth/zoho/user-jobs`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch jobs");
        const list = Array.isArray(data?.data?.jobs) ? data.data.jobs : [];
        setJobs(list);
        try {
          localStorage.setItem("lastJobs", JSON.stringify(list));
        } catch {}
      } catch (e) {
        setError(e.message || "Failed to fetch jobs");
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs
      .filter((j) => {
        if (statusFilter !== "All") {
          return (
            (j?.Job_Status || "").toLowerCase() === statusFilter.toLowerCase()
          );
        }
        return true;
      })
      .filter((j) => {
        if (!q) return true;
        const hay = `${j?.CTR_Number || ""} ${
          j?.BL_Number || j?.Bill_of_Lading_Number || ""
        } ${j?.Job_Number || ""}`.toLowerCase();
        return hay.includes(q);
      });
  }, [jobs, statusFilter, search]);

  const getId = (item, idx) => item?.id || item?._id || item?.Job_Number || idx;

  const openDetail = (item, idx) => {
    const id = encodeURIComponent(String(getId(item, idx)));
    navigate(`/tracking/${id}`, { state: { job: item } });
  };

  if (!isLoggedIn) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-semibold text-[#111827] mb-4">Tracking</h1>

      <div className="flex flex-wrap gap-2 mb-3">
        {STATUS_OPTIONS.map((opt) => {
          const active = opt === statusFilter;
          return (
            <button
              key={opt}
              onClick={() => setStatusFilter(opt)}
              className={`px-3 py-2 rounded-2xl text-sm font-medium ${
                active ? "bg-[#003F75] text-white" : "bg-gray-100 text-gray-700"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search container / BL / Project"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 mb-4"
      />

      {loading ? (
        <p className="text-center text-gray-500 py-10">Loading...</p>
      ) : error ? (
        <p className="text-center text-red-600 py-10">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="text-center text-gray-500 py-10">No jobs found</p>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map((item, idx) => (
            <button
              key={getId(item, idx)}
              onClick={() => openDetail(item, idx)}
              className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-[#003F75] font-bold">
                  {item?.Job_Status || "-"}
                </span>
                <span className="text-gray-500">
                  {item?.ETA || item?.ETA_Date || "--"}
                </span>
              </div>
              <div className="mt-2 font-bold text-gray-900">
                Project #{item?.Job_Number || item?.ERP_No || "--"}
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex">
                  <span className="text-gray-500 w-36">Trade Region:</span>
                  <span className="text-gray-900">
                    {item?.Trade_Region || "-"}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-36">Supplier:</span>
                  <span className="text-gray-900">
                    {item?.Supplier_Name || "-"}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-36">BOE Date:</span>
                  <span className="text-gray-900">
                    {item?.Date_of_Bill_of_Entry || "-"}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-36">Contact:</span>
                  <span className="text-gray-900">
                    {item?.Contact_Name?.name || "-"}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrackingList;
